import { useEffect } from 'react';

import StepNavigationButtons from './StepNavigationButtons';
import YourDetailsFields from './StepFields/YourDetailsFields';
import CallDetailsFields from '@evercall/call-form/ui/CallDetailsFields';
import CallFormatFields from './StepFields/CallFormatFields';
import AddOnsFields from './StepFields/AddOnsFields';

import {
  ICallFormStep,
  TScheduleCallFormInitialValues,
  TScheduleCallFormValues,
} from '../interfaces';
import { CALL_FORM_STEPS } from '../constants';

import finsightWebUrl from '@/finsight/infrastructure/finsightWebUrl';
import getCallDetailsInitialValues from '@evercall/call-form/ui/helpers/getCallDetailsInitialValues';
import {
  useScheduleYourCallFormContext,
} from '@/evercall/application/scheduleYourCallForm/ScheduleYourCallFormContext';
import { useForm } from '@framework/ui/Organisms/FinalForm';
import { getPlatformOption } from '@evercall/call-form/ui/helpers/getPlatformOption';
import { useTimeZoneContext } from '@framework/timezone/TimeZoneContext';
import { mapOptionToValue } from '@framework/data-helpers/selectOptionMappers';
import { CallEventType } from '@evercall/domain/vo/call/CallEventType';

import styles from './callFormSteps.module.scss';
import scheduleYourCallStyles from '../scheduleYourCall.module.scss';

interface IProps {
  scrollToTop: () => void,
  currentStep: ICallFormStep,
  setCurrentStep: (payload: ICallFormStep) => void,
}

const CallFormSteps = (
  {
    scrollToTop,
    currentStep,
    setCurrentStep,
  }: IProps,
) => {
  const {
    state: {
      transactionTypeList,
      countryList,
      platformList,
    },
  } = useScheduleYourCallFormContext();
  const { timeZoneList, timeZoneOptions } = useTimeZoneContext();

  const form = useForm<TScheduleCallFormValues, TScheduleCallFormInitialValues>();
  const { values } = form.getState();
  const eventTypeId = mapOptionToValue(values?.eventTypeId) as CallEventType;

  useEffect(
    () => {
      form.initialize(
        {
          additionalNotes: '',
          webinarPlatformId: getPlatformOption(platformList),
          speakers: [{ name: '', email: '' }],
          ...getCallDetailsInitialValues(timeZoneList, countryList),
        },
      );
    },
    [],
  );

  const renderStep = () => {
    switch (currentStep) {
      case CALL_FORM_STEPS.YOUR_DETAILS:
        return (
          <YourDetailsFields />
        );
      case CALL_FORM_STEPS.CALL_DETAILS:
        return (
          <CallDetailsFields
            fieldsContainerCls={ styles.callDetailsFieldsContainer }
            dialInCountriesContainerCls={ styles.dialInCountriesContainerCls }
            dialInCountriesLabelCls={ styles.landingTableTitle }
            // lists
            timeZoneOptions={ timeZoneOptions }
            countryList={ countryList }
            transactionTypeList={ transactionTypeList }
            platformList={ platformList }
          />
        );
      case CALL_FORM_STEPS.CALL_FORMAT:
        return (
          <CallFormatFields />
        );
      case CALL_FORM_STEPS.ADD_ONS:
        return (
          <AddOnsFields />
        );
      default:
        return null;
    }
  };

  const contactUsLink = (
    <a
      href={ finsightWebUrl.getContactUsUrl() }
      target="_blank"
      className={ scheduleYourCallStyles.subtitleLink }
      data-test="scheduleYourCallContactUsLink"
    >
      Contact us
    </a>
  );

  return (
    <div>
      <h3 className={ styles.stepHeaderTitle } data-test="stepTitle">{ currentStep.title }</h3>
      <div className={ styles.formSectionSubtitle }>
        { currentStep.subtitle(eventTypeId) }
      </div>
      { renderStep() }
      <StepNavigationButtons
        scrollToTop={ scrollToTop }
        currentStep={ currentStep }
        setCurrentStep={ setCurrentStep }
      />
      <div className={ styles.contactUsLabel }>
        Don’t like filling forms? { contactUsLink } by email or phone to schedule your event. Available 24/7. <br />
        Events must be scheduled at least 6 hours in advance. A 50% fee applies if an event is canceled or rescheduled
        within 24 hours of its start time.
      </div>
    </div>
  );
};

export default CallFormSteps;
